import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";

import { startBuyerSignin } from "@/features/buyerAuth";

// Both keys optional (conditional spread) so callers can pass either, both, or
// neither without TS demanding the other. `register` just reframes the copy;
// the passwordless flow is the same (email → one-tap link → buyer row on verify).
const SearchParams = (
  search: Record<string, unknown>,
): { return?: string; mode?: "register" } => ({
  ...(typeof search.return === "string" ? { return: search.return } : {}),
  ...(search.mode === "register" ? { mode: "register" as const } : {}),
});

// TNK-167 phase C — single-field login form. On submit, calls
// /api/buyer/auth/start and flips into a "check your email" confirmation.
// The verify route (shop.account.verify.tsx) consumes the magic link and
// honors the optional `?return=<path>` so the buyer can be bounced back
// to whatever surface they were trying to reach (e.g., `/shop/reserve`).
export const Route = createFileRoute("/shop/account/login")({
  component: ShopAccountLogin,
  validateSearch: SearchParams,
});

function ShopAccountLogin() {
  const { return: returnTo, mode } = Route.useSearch();
  const isRegister = mode === "register";
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      await startBuyerSignin(trimmed);
      setSent(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not send sign-in link.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="mx-auto max-w-md space-y-4 py-12 text-center">
        <h1 className="shop-display text-2xl font-bold text-foreground">
          Check your email
        </h1>
        <p className="text-sm text-muted-foreground">
          We sent a sign-in link to{" "}
          <span className="font-medium text-foreground">{email.trim()}</span>.
          Click the link in the email to finish signing in.
        </p>
        <p className="text-xs text-muted-foreground">
          The link expires in 15 minutes. You can{" "}
          <button
            type="button"
            onClick={() => {
              setSent(false);
              setError(null);
            }}
            className="text-primary underline hover:no-underline"
          >
            try a different address
          </button>{" "}
          if needed.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md space-y-6 py-12">
      <div className="space-y-1 text-center">
        <h1 className="shop-display text-2xl font-bold text-foreground">
          {isRegister ? "Create your account" : "Sign in"}
        </h1>
        <p className="text-sm text-muted-foreground">
          {isRegister
            ? "Enter your email and we'll send a one-tap link to finish — no password to create."
            : "We'll email you a one-click sign-in link. No password needed."}
        </p>
      </div>
      <form
        onSubmit={submit}
        className="space-y-4 rounded-lg border border-border bg-card p-5"
      >
        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-foreground">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="you@example.com"
          />
        </label>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {submitting
            ? "Sending…"
            : isRegister
              ? "Create account"
              : "Send sign-in link"}
        </button>
      </form>

      <p className="text-center text-xs text-muted-foreground">
        {isRegister ? (
          <>
            Already have an account?{" "}
            <Link
              to="/shop/account/login"
              search={{ return: returnTo, mode: undefined }}
              className="font-medium text-primary hover:underline"
            >
              Sign in
            </Link>
          </>
        ) : (
          <>
            New here?{" "}
            <Link
              to="/shop/account/login"
              search={{ return: returnTo, mode: "register" }}
              className="font-medium text-primary hover:underline"
            >
              Create an account
            </Link>
          </>
        )}
      </p>

      {returnTo && (
        <p className="text-center text-xs text-muted-foreground">
          After sign-in we'll return you to{" "}
          <code className="rounded bg-muted px-1.5 py-0.5">{returnTo}</code>.
        </p>
      )}
      <p className="text-center text-xs text-muted-foreground">
        <Link
          to="/shop/inventory"
          className="text-muted-foreground hover:text-foreground"
        >
          ← Back to the store
        </Link>
      </p>
    </div>
  );
}
